// getVipLevelLivestreamStyleList.js
'use strict';
import { Collection } from 'immutable';
import createCachedSelector from '../resource/createCachedSelector.js';

import getRemoteConfigData from '../selector/getRemoteConfigData.js';

const VIP_LEVEL_LIVESTREAM_STYLE_KEY_TEMPLATE = 'VIP_LEVEL_$_LIVESTREAM_STYLE';
const VIP_LEVEL_LIVESTREAM_CHAT_LOG_STYLE_KEY_TEMPLATE =
  'VIP_LEVEL_$_LIVESTREAM_CHAT_LOG_STYLE';

/**
 * Get all livestream style configs for each VIP level.
 *
 * This selector iterates through the given user level benefit threshold list
 * and retrieves the corresponding remote config values for:
 * - VIP_LEVEL_{level}_LIVESTREAM_STYLE
 * - VIP_LEVEL_{level}_LIVESTREAM_CHAT_LOG_STYLE
 *
 * For example: lv1 ~ lv11 will map to their respective livestream
 * style and chat log style configs.
 *
 * @kind selector
 * @param {Immutable.Map} state - root redux state.
 * @param {Array<Object>} userLevelBenefitThresholdList - list of VIP level configs.
 * @return {Array<Array<Object>>}
 * An array grouped by VIP level.
 *
 * Each outer array item represents a single VIP level.
 * The inner array structure:
 * - index 0: livestream user entered notice style config
 * - index 1: livestream chat message log style config
 */
const getVipLevelLivestreamStyleList = createCachedSelector(
  (state, userLevelBenefitThresholdList) =>
    userLevelBenefitThresholdList.reduce((prev, curr) => {
      const VIP_LEVEL_LIVESTREAM_STYLE_KEY =
        VIP_LEVEL_LIVESTREAM_STYLE_KEY_TEMPLATE.replace('$', curr.level);
      const VIP_LEVEL_LIVESTREAM_CHAT_LOG_STYLE_KEY =
        VIP_LEVEL_LIVESTREAM_CHAT_LOG_STYLE_KEY_TEMPLATE.replace(
          '$',
          curr.level
        );
      prev.push([
        getRemoteConfigData(state, VIP_LEVEL_LIVESTREAM_STYLE_KEY, undefined, {
          WHITESPACE: '/**/',
        }),
        getRemoteConfigData(
          state,
          VIP_LEVEL_LIVESTREAM_CHAT_LOG_STYLE_KEY,
          undefined,
          { WHITESPACE: '/**/' }
        ),
      ]);
      return prev;
    }, []),
  vipLevelLivestreamStyleList => {
    const result = vipLevelLivestreamStyleList;
    return result instanceof Collection ? result.toJS() : result;
  }
)(
  (state, userLevelBenefitThresholdList) =>
    `${userLevelBenefitThresholdList.join('')}`
);

export default getVipLevelLivestreamStyleList;
